/**
 * Billable-hours increment reader — the FRONTEND side of the WS-8 config
 * seam (founder CORRECTION 2026-07-10: "We absolutely bill in 6 minute
 * increments! 0.1, 0.2, etc." — the original 15-minute item was a
 * feedback mistake; the fallback below is the corrected value).
 *
 * The increment lives in tenants.config `billing.hours_increment` (a
 * decimal string, e.g. '0.1' = 6 minutes), read with a code fallback of
 * '0.1' (ADR-0009 config-seam convention; readRateBands precedent — no
 * config row set for Peace, behavior is byte-identical to the default).
 *
 * Byte-mirror of api/_shared/hoursIncrement.ts (the server enforces the
 * same rule at the note write/edit paths). Pure; unit-tested.
 */

/** Platform default: 6-minute steps (founder correction 2026-07-10). */
export const DEFAULT_HOURS_INCREMENT = '0.1';

/** A well-formed increment: parses to a finite number, > 0, at most 2
 *  decimal places (hours are numeric(6,2) on the wire). */
function isWellFormedIncrement(v: unknown): v is string {
  if (typeof v !== 'string' || v.trim() === '') return false;
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return false;
  return Math.abs(n * 100 - Math.round(n * 100)) < 1e-9;
}

/**
 * Read the tenant's billable-hours increment from a tenants.config value.
 * Defensive walk (readRateBands precedent): anything short of a
 * well-formed positive decimal string falls back to the platform default.
 */
export function readHoursIncrement(config: unknown): string {
  if (typeof config !== 'object' || config === null) return DEFAULT_HOURS_INCREMENT;
  const billing = (config as Record<string, unknown>).billing;
  if (typeof billing !== 'object' || billing === null) return DEFAULT_HOURS_INCREMENT;
  const v = (billing as Record<string, unknown>).hours_increment;
  return isWellFormedIncrement(v) ? v.trim() : DEFAULT_HOURS_INCREMENT;
}

/**
 * True when `hours` sits on the increment grid. Integer-cents math (both
 * values are at most 2dp), so 0.3 % 0.1 float noise can never mis-answer.
 * 0 is always on the grid (a no-charge visit stays valid).
 */
export function isOnHoursIncrement(hours: number, increment: string): boolean {
  if (!Number.isFinite(hours)) return false;
  const hc = Math.round(hours * 100);
  const ic = Math.round(Number(increment) * 100);
  if (ic <= 0) return true; // defensive: a broken increment never blocks
  return Math.abs(hours * 100 - hc) < 1e-6 && hc % ic === 0;
}

/**
 * The plain-words refusal copy, friendly by design (founder 2026-07-10:
 * "make sure the copy is friendly"): states the rule AND hands over the
 * fix by naming the nearest on-grid values when the entered hours are
 * known. "Time is billed in 6-minute steps. Closest would be 1.2 or 1.3."
 * (Periods, never em dashes: brand rule #1.)
 */
export function hoursIncrementMessage(increment: string, hours?: number): string {
  const minutes = Number(increment) * 60;
  const rule = Number.isInteger(minutes)
    ? `Time is billed in ${minutes}-minute steps`
    : `Time is billed in steps of ${increment} hours`;
  const neighbors = nearestOnIncrement(hours, increment);
  if (neighbors === null) {
    // "Tenths" is true only of the 0.1 platform default; another increment
    // keeps the plain rule with its step.
    return Number(increment) === 0.1 ? `${rule}. Enter hours in tenths: 0.5, 1.2.` : `${rule} (${increment}).`;
  }
  return `${rule}. Closest would be ${neighbors}.`;
}

/** "1.2 or 1.3": the entered value's on-grid neighbors; null when hours
 *  are absent/invalid/already on-grid. */
function nearestOnIncrement(hours: number | undefined, increment: string): string | null {
  if (hours === undefined || !Number.isFinite(hours) || hours < 0) return null;
  const ic = Math.round(Number(increment) * 100);
  if (ic <= 0) return null;
  const hc = Math.round(hours * 100);
  if (hc % ic === 0) return null;
  const lo = Math.floor(hc / ic) * ic;
  const hi = lo + ic;
  const fmt = (c: number): string => String(c / 100);
  return lo > 0 ? `${fmt(lo)} or ${fmt(hi)}` : fmt(hi);
}
